import { inject, Injectable } from '@angular/core';
import { TwitchChatStateService } from './twitch-chat-state.service.js';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private readonly state = inject(TwitchChatStateService);

  exportClipsTxt(): void {
    const highlightsToExport = this.state.exportableHighlights();
    const videoIdPrefixed = this.getPrefixedVideoId();
    if (highlightsToExport.length === 0 || !videoIdPrefixed) return;

    const videoId = videoIdPrefixed.startsWith('v') ? videoIdPrefixed.substring(1) : videoIdPrefixed;

    let content = `Экспорт клипов из Twitch VOD: ${this.state.videoTitle() || 'Без названия'}\n`;
    content += `Стример: ${this.state.streamerName() || 'Неизвестно'}\n`;
    content += `VOD ID: ${videoId}\n\n`;
    content += `========================================\n\n`;

    highlightsToExport.forEach((highlight, index) => {
      const twitchStartTime = this.formatTimeForTwitch(highlight.clipStartTime);
      const twitchLink = `https://www.twitch.tv/videos/${videoId}?t=${twitchStartTime}`;

      content += `Клип #${index + 1}: ${highlight.isAnalyzed ? highlight.description : 'Не проанализировано'}\n`;
      content += `Таймкод: ${this.formatTime(highlight.clipStartTime)} - ${this.formatTime(highlight.clipEndTime)}\n`;
      content += `Длительность: ${Math.round(highlight.clipEndTime - highlight.clipStartTime)} секунд\n`;
      if (highlight.isAnalyzed) {
        content += `Описание: ${highlight.description}\n`;
      }
      content += `Статус: ${highlight.isPinned ? 'Закреплён' : 'Обычный'}\n`
      content += `Ссылка для перехода: ${twitchLink}\n\n`;
      content += `----------------------------------------\n\n`;
    });

    this.downloadFile(content, `twitch_clips_${this.state.streamerName()}_${videoId}.txt`, 'text/plain;charset=utf-8');
  }

  exportDownloadScript(platform: 'windows' | 'unix'): void {
    const highlightsToExport = this.state.exportableHighlights();
    const videoId = (this.getPrefixedVideoId() || '').substring(1);
    if (highlightsToExport.length === 0 || !videoId) return;

    const streamerNameSafe = (this.state.streamerName() || 'streamer').replace(/[^a-zA-Z0-9_.]/g, '_');
    const trimModeArg = '--trim-mode Safe';

    let commands: string[];
    let content = '';
    let fileName = '';

    if (platform === 'windows') {
      content = `@echo off\nchcp 65001 > nul\n\n`;
      content += `echo Creating 'clips' directory...\n`;
      content += `if not exist "%~dp0clips" mkdir "%~dp0clips"\n\n`;

      commands = highlightsToExport.map(highlight => {
        const startSeconds = Math.floor(highlight.clipStartTime);
        const endSeconds = Math.floor(highlight.clipEndTime);
        // Name by clipStartTime (the actual content start), not startTime (the
        // immutable peak position) — keeps filenames consistent with cloud
        // single-clip + ZIP downloads, and the timestamp matches what -b/-e
        // actually extracts when the user has dragged borders.
        const fileNameTimestamp = this.formatTime(highlight.clipStartTime).replace(/:/g, '-');
        const outputFilename = `"%~dp0clips\\${streamerNameSafe}_${videoId}_clip_${fileNameTimestamp}.mp4"`;
        return `TwitchDownloaderCLI videodownload --id ${videoId} --output ${outputFilename} -b ${startSeconds} -e ${endSeconds} ${trimModeArg}`;
      });

      content += `echo Starting download of ${commands.length} clips for VOD ID ${videoId}...\n\n`;
      content += commands.join('\n\n');
      content += '\n\necho All downloads finished.\npause';
      fileName = 'download_clips.bat';
    } else { // unix
      content = '#!/bin/bash\n\n';
      content += 'SCRIPT_DIR=$( cd -- "$( dirname -- "${BASH_SOURCE[0]}" )" &> /dev/null && pwd )\n\n';
      content += 'echo "Creating \'clips\' directory..."\n';
      content += 'mkdir -p "$SCRIPT_DIR/clips"\n\n';

      commands = highlightsToExport.map(highlight => {
        const startSeconds = Math.floor(highlight.clipStartTime);
        const endSeconds = Math.floor(highlight.clipEndTime);
        // See windows branch — naming by clipStartTime keeps parity with cloud paths.
        const fileNameTimestamp = this.formatTime(highlight.clipStartTime).replace(/:/g, '-');
        const outputFilename = `"$SCRIPT_DIR/clips/${streamerNameSafe}_${videoId}_clip_${fileNameTimestamp}.mp4"`;
        return `TwitchDownloaderCLI videodownload --id ${videoId} --output ${outputFilename} -b ${startSeconds} -e ${endSeconds} ${trimModeArg}`;
      });

      content += `echo "Starting download of ${commands.length} clips for VOD ID ${videoId}..."\n\n`;
      content += commands.join('\n');
      content += '\n\necho "All downloads finished."';
      fileName = 'download_clips.sh';
    }

    this.downloadFile(content, fileName, 'application/octet-stream');
  }

  private getPrefixedVideoId(): string | null {
    const id = this.state.videoId();
    if (id === null) return null;
    const idStr = String(id);
    return idStr.startsWith('v') ? idStr : `v${idStr}`;
  }

  private downloadFile(content: string, fileName: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  formatTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  formatTimeForTwitch(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${hours}h${minutes}m${seconds}s`;
  }
}